import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SimpleDialogService, CommonActionKey, FilterParameter, QueryParamsService } from 'common';
import { readOnly, readWrite } from '../../user/user-permission/user-permission.data';
import { UserPermissionService } from '../../user/user-permission/user-permission.service';
import { BankingCustomerListFilterDialogComponent } from '../banking-customer-list-filter-dialog/banking-customer-list-filter-dialog.component';
import { BankingCustomerListFilterService } from '../banking-customer-list-filter-dialog/banking-customer-list-filter.service';
import { BankingCustomerQueryResult, BankingCustomerFilters, BankingCustomerQueryParams } from '../banking.model';
import { BankingService } from '../banking.service';

@UntilDestroy()
@Component({
    selector: 'ifb-banking-customer-list',
    templateUrl: './banking-customer-list.component.html',
    styleUrls: ['./banking-customer-list.component.scss'],
    standalone: false
})
export class BankingCustomerListComponent implements OnInit {
  @ViewChild(MatPaginator, { static: true })
  paginator: MatPaginator;

  tableColumns: string[] = ['createdDate', 'username', 'id', 'name', 'bank', 'delete'];

  filters: BankingCustomerFilters;
  activeFilters: FilterParameter[] = [];
  queryParams: BankingCustomerQueryParams;
  data: BankingCustomerListComponentData;
  canDeleteBankCustomer = false;
  canReadBankingAccount = false;
  provider: string;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly userPermissionService: UserPermissionService,
    private readonly bankingCustomerListFilterService: BankingCustomerListFilterService,
    private readonly bankingService: BankingService,
    private readonly queryParamsService: QueryParamsService,
    private readonly simpleDialogService: SimpleDialogService,
  ) {}

  ngOnInit(): void {
    this.route.data.pipe(untilDestroyed(this)).subscribe(this.dataInit.bind(this));
    this.route.queryParams.pipe(untilDestroyed(this)).subscribe((it) => {
      this.queryParams = this.queryParamsService.init(it);
      this.activeFilters = this.bankingCustomerListFilterService.addFilterParameters(this.queryParams);
      this.filters = new BankingCustomerFilters(this.queryParams);
    });
    this.userPermissionService
      .granted([readWrite('servicing-delete-synced-customer')])
      .subscribe((res) => (this.canDeleteBankCustomer = res));
    this.userPermissionService
      .granted([readOnly('servicing-synced-account')])
      .subscribe((res) => (this.canReadBankingAccount = res));
    this.paginator.pageSize = this.queryParams.limit;
    this.paginator.pageIndex = this.queryParams.skip / this.queryParams.limit;
  }

  delete(customerId: string): void {
    if (!customerId) return;
    this.simpleDialogService
      .openSimpleConfirmDialog('Delete customer?', 'Confirm')
      .subscribe((result: CommonActionKey) => {
        if (result === CommonActionKey.Confirm) {
          this.bankingService.deleteCustomer(this.provider, customerId).subscribe(() => {
            this.fetchData();
          });
        }
      });
  }

  fetchData(): void {
    this.bankingService
      .getCustomers(this.provider, this.queryParams)
      .pipe(untilDestroyed(this))
      .subscribe((res) => (this.data.bankCustomerQueryResult = res));
  }

  activeFilterRemoved(activefilters: FilterParameter[]): void {
    if (!activefilters.length) return;
    activefilters.forEach((item) => {
      this.filters.remove(item.id);
      this.bankingCustomerListFilterService.filter(this.provider, this.filters);
    });
  }

  filterDialog(): void {
    BankingCustomerListFilterDialogComponent.show(this.dialog, this.queryParams).subscribe((result) => {
      if (result) {
        this.filters = result;
        this.paginator.firstPage();
        this.bankingCustomerListFilterService.filter(this.provider, result);
      }
    });
  }

  bankCustomerAccountListLink(bankingCustomerId: string): void {
    if (!bankingCustomerId || bankingCustomerId === undefined || !this.canReadBankingAccount) return;
    this.router.navigate([`banking/${this.provider}/customers/${bankingCustomerId}/accounts`]);
  }

  private dataInit(data: BankingCustomerListComponentData): void {
    this.data = data;
    this.provider = this.route.snapshot.paramMap.get('provider');
  }

  setPage(): void {
    this.bankingCustomerListFilterService.filter(this.provider, this.filters, this.paginator);
  }
}

export interface BankingCustomerListComponentData {
  bankCustomerQueryResult: BankingCustomerQueryResult;
}
